/*
  Peter Augustus homepage build | GSAP registry
  13 Oct 2026 | Theme 138814587088

  Loads after pa-vendor-gsap.min.js and pa-vendor-scrolltrigger.min.js.
  Every animated component is an element with data-pa-anim="<name>". Its
  script calls PAHome.register(name, init). init(el, conditions) runs inside
  a gsap.matchMedia() scoped to that element, so selector text inside init
  only reaches that component, and every tween and ScrollTrigger it creates
  is reverted when the media query changes or the theme editor unloads the
  section. init may return a cleanup function for anything GSAP does not own
  (event listeners, observers). Work that has to wait (fonts, images) goes
  through the third argument, the matchMedia context: context.add(() => ...).

  conditions: isDesktop, isMobile, navDesktop (header layout, 1280px), reduceMotion, designMode
*/
(() => {
  if (!window.gsap) return;

  const plugins = [window.ScrollTrigger, window.SplitText, window.CustomEase, window.Flip, window.Draggable, window.InertiaPlugin, window.Observer].filter(Boolean);
  if (plugins.length) gsap.registerPlugin(...plugins);
  // Osmo's ease for text reveals: slow start, quick finish, soft landing
  if (window.CustomEase) window.CustomEase.create('osmo-ease', '0.625, 0.05, 0, 1');
  gsap.defaults({ ease: 'power3.out', duration: 0.8 });
  document.documentElement.classList.add('pa-gsap-ready');

  const CONDITIONS = {
    isDesktop: '(min-width: 1024px)',
    isMobile: '(max-width: 1023px)',
    navDesktop: '(min-width: 1280px)',
    reduceMotion: '(prefers-reduced-motion: reduce)'
  };
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);
  const registry = new Map();
  const mounted = new Map();

  function mount(el) {
    const init = registry.get(el.dataset.paAnim);
    if (!init || mounted.has(el)) return;
    const mm = gsap.matchMedia();
    mm.add(CONDITIONS, (context) => init(el, { ...context.conditions, designMode }, context), el);
    mounted.set(el, mm);
  }

  function unmount(el) {
    const mm = mounted.get(el);
    if (!mm) return;
    mm.revert();
    mounted.delete(el);
  }

  function scan(root) {
    if (root.matches && root.matches('[data-pa-anim]')) mount(root);
    root.querySelectorAll('[data-pa-anim]').forEach(mount);
  }

  // A refresh measures the whole page. While the preloader plays it waits until the
  // preloader is gone, so the circle opens without a stall (sections/pa-preloader.liquid).
  // ScrollTrigger's own refresh on load is off for the same reason; the load listener
  // below does it instead.
  if (window.ScrollTrigger) window.ScrollTrigger.config({ autoRefreshEvents: 'visibilitychange,DOMContentLoaded,resize' });
  let refreshQueued = false;
  function refresh() {
    if (!window.ScrollTrigger || refreshQueued) return;
    refreshQueued = true;
    const calm = (window.PAPreloader && window.PAPreloader.gone) || Promise.resolve();
    calm.then(() => {
      refreshQueued = false;
      window.ScrollTrigger.refresh();
    });
  }

  window.PAHome = {
    register(name, init) {
      registry.set(name, init);
      document.querySelectorAll(`[data-pa-anim="${name}"]`).forEach(mount);
    },
    refresh
  };

  // Theme editor re-renders a section without reloading the page
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-pa-anim]').forEach(unmount);
  });

  // Trigger positions move once Aviano and the images above the fold arrive
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(refresh);
  window.addEventListener('load', refresh, { once: true });

  // Lazy images and app widgets keep growing the page after load, which left the
  // footer parallax 26px short of its end on the preview. Refresh when the page
  // height changes, debounced so a burst of image loads costs one refresh.
  if (window.ResizeObserver && window.ScrollTrigger) {
    let lastHeight = 0;
    let timer = 0;
    new ResizeObserver(() => {
      const height = document.documentElement.scrollHeight;
      if (Math.abs(height - lastHeight) < 2) return;
      lastHeight = height;
      clearTimeout(timer);
      timer = setTimeout(refresh, 200);
    }).observe(document.body);
  }
})();
